import { useState } from "react";
import { Button, Modal, Pressable, Typography } from "@repo/design-system";

interface IZoomableImageProps {
  src: string;
  alt: string;
  caption?: string;
}

/**
 * Thumbnail that opens its full-size image in a same-window Modal. Inside
 * the modal, clicking the image toggles between "fit to screen" and its
 * native pixel size (wrapped in a scrollable container) — a same-window
 * equivalent of a lightbox zoom.
 */
export function ZoomableImage({ src, alt, caption }: IZoomableImageProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isZoomed, setIsZoomed] = useState(false);

  const handleClose = () => {
    setIsOpen(false);
    setIsZoomed(false);
  };

  return (
    <>
      <div className="flex flex-col gap-2">
        <Pressable
          onPress={() => setIsOpen(true)}
          className="overflow-hidden rounded-xl border border-borderPrimary shadow-light">
          <img src={src} alt={alt} className="w-full object-contain" />
        </Pressable>
        {caption && (
          <Typography
            variant="paragraph-m-regular"
            className="text-center text-contentTertiary">
            {caption}
          </Typography>
        )}
      </div>

      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        title={alt}
        size="full"
        isDismissable>
        <div className="flex max-h-[90vh] flex-col">
          <div className="flex items-center justify-between border-b border-borderPrimary px-6 py-4">
            <Typography
              variant="paragraph-l-semibold"
              className="text-contentPrimary">
              {caption ?? alt}
            </Typography>
            <Button
              variant="transparent"
              size="small"
              leftIcon="close"
              onPress={handleClose}
            />
          </div>

          {/* Plain block flow + margin auto (not flex justify-center) — when
              the zoomed image is wider than this container, flex centering
              would push its left edge to a negative scroll offset that
              overflow:auto can never scroll back to, permanently hiding it. */}
          <div className="overflow-auto bg-backgroundSecondary p-4">
            <Pressable
              onPress={() => setIsZoomed((zoomed) => !zoomed)}
              className="block w-fit mx-auto">
              <img
                src={src}
                alt={alt}
                className={
                  isZoomed
                    ? "w-auto max-w-none cursor-zoom-out"
                    : "max-h-[72vh] w-auto cursor-zoom-in object-contain"
                }
              />
            </Pressable>
          </div>

          <div className="border-t border-borderPrimary px-6 py-2">
            <Typography
              variant="paragraph-m-regular"
              className="text-center text-contentTertiary">
              Click the image to {isZoomed ? "zoom out" : "zoom in"}
            </Typography>
          </div>
        </div>
      </Modal>
    </>
  );
}
